import maplibregl from "maplibre-gl";
import { VECTOR_STYLE_URL } from "../../shared/basemap.ts";
import { spotCoordinates } from "../../shared/quest-feed.ts";
import { mapHasLayer, mapStyleReady, waitMapIdle, waitMapStyleReady } from "../../shared/map-style-safe.ts";
import { routeCanvasBearing } from "../../shared/route-canvas.ts";
import { fitRotated } from "../../shared/rotated-fit.ts";
import { routeDisplayFitPoints, type RouteFillFeature, type RoutePaintFeature } from "../../shared/quest-route-paint.ts";
import type { LngLat, WorkArea } from "../../shared/types.ts";
import {
  listSpotThumbFitView,
  snapshotMapWithSpotMarkers,
  type ListSpotThumbPreview,
} from "./list-spot-thumb-composite.ts";
import {
  applyStyledRouteLineScaleForContainer,
  ensureStyledRouteLayers,
  setStyledRouteData,
  styledRouteLayerIds,
} from "./styled-route-layers.ts";

/** Route list cards — unchanged since pre–#47 (styled route paint + fill on vector basemap). */
export const THUMB_RENDER_VERSION = 6;

/** Spot-marker list cards (2D composite); bumped separately from route thumbs. */
export const SPOT_LIST_THUMB_RENDER_VERSION = 7;

export const THUMB_CSS_W = 96;
export const THUMB_CSS_H = 68;

const MAX_ZOOM = 15;
const PADDING = 10;
const LAYER_IDS = styledRouteLayerIds("list-thumb");

const PLACEHOLDER_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="96" height="68" viewBox="0 0 96 68"><rect fill="#dde3ea" width="96" height="68"/><path fill="#b8c2cc" d="M48 18c-8.8 0-16 7.2-16 16 0 12 16 28 16 28s16-16 16-28c0-8.8-7.2-16-16-16zm0 22a6 6 0 110-12 6 6 0 010 12z"/></svg>`;

export const QUEST_LIST_THUMB_PLACEHOLDER = `data:image/svg+xml,${encodeURIComponent(PLACEHOLDER_SVG)}`;

export type QuestListThumbMode = "route" | "spot" | "placeholder";

export type { ListSpotThumbPreview };

export type RouteThumbnailRequest =
  | {
      id: string;
      mode: "route";
      coordinates: LngLat[];
      canvas?: WorkArea | null;
      paint?: RoutePaintFeature[] | null;
      fill?: RouteFillFeature[] | null;
    }
  | { id: string; mode: "spot"; spots: readonly ListSpotThumbPreview[] }
  | { id: string; mode: "placeholder" };

const cache = new Map<string, string>();
const inflight = new Map<string, Promise<string>>();

let host: HTMLElement | null = null;
let map: maplibregl.Map | null = null;
let queue: Array<{ req: RouteThumbnailRequest; resolve: (url: string) => void; reject: (err: unknown) => void }> = [];
let pumping = false;
let hostSizeKey = "";

const thumbObservers = new Map<Element | null, IntersectionObserver>();

function listScrollRoot(root: ParentNode): Element | null {
  return root instanceof Element &&
    (root.classList.contains("nearby-list") || root.classList.contains("piece-list-items"))
    ? root
    : null;
}

function thumbObserverFor(root: ParentNode): IntersectionObserver {
  const scrollRoot = listScrollRoot(root);
  const hit = thumbObservers.get(scrollRoot);
  if (hit) return hit;
  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const img = entry.target as HTMLImageElement;
        io.unobserve(img);
        void hydrateThumb(img);
      }
    },
    { root: scrollRoot, rootMargin: "120px" },
  );
  thumbObservers.set(scrollRoot, io);
  return io;
}

function thumbRenderSize(): { w: number; h: number; cacheKey: string } {
  const dpr = Math.min(Math.max(window.devicePixelRatio || 1, 1), 3);
  const w = Math.round(THUMB_CSS_W * dpr);
  const h = Math.round(THUMB_CSS_H * dpr);
  return { w, h, cacheKey: `${w}x${h}` };
}

function ensureMap(w: number, h: number): maplibregl.Map {
  const sizeKey = `${w}x${h}`;
  if (map && hostSizeKey === sizeKey) return map;
  if (map) {
    map.remove();
    map = null;
    host?.remove();
    host = null;
  }
  hostSizeKey = sizeKey;
  host = document.createElement("div");
  host.className = "route-thumb-pool-host";
  host.style.cssText = `position:fixed;left:-9999px;top:0;width:${w}px;height:${h}px;overflow:hidden;pointer-events:none;opacity:0;`;
  document.body.append(host);
  map = new maplibregl.Map({
    container: host,
    style: VECTOR_STYLE_URL,
    center: [126.98, 37.57],
    zoom: 12,
    attributionControl: false,
    interactive: false,
    fadeDuration: 0,
    canvasContextAttributes: { preserveDrawingBuffer: true },
  } as maplibregl.MapOptions);
  return map;
}

function thumbRouteLayersReady(m: maplibregl.Map): boolean {
  return mapStyleReady(m) && mapHasLayer(m, LAYER_IDS.solid);
}

export function cacheId(req: RouteThumbnailRequest, sizeKey: string): string {
  if (req.mode === "spot") return `v${SPOT_LIST_THUMB_RENDER_VERSION}:spot:${req.id}@${sizeKey}`;
  if (req.mode === "placeholder") return `placeholder:${req.id}`;
  return `v${THUMB_RENDER_VERSION}:${req.id}@${sizeKey}`;
}

/** @deprecated alias for tests */
export const questListThumbCacheKey = cacheId;

async function renderRouteThumb(m: maplibregl.Map, req: Extract<RouteThumbnailRequest, { mode: "route" }>, w: number, h: number): Promise<string> {
  const bearing = routeCanvasBearing(req.canvas ?? null);
  const line = req.coordinates;
  const paint = req.paint ?? null;
  ensureStyledRouteLayers(m, LAYER_IDS);
  setStyledRouteData(m, LAYER_IDS, paint, line, undefined, req.fill ?? null);
  applyStyledRouteLineScaleForContainer(m, LAYER_IDS, w, h);
  if (!thumbRouteLayersReady(m)) {
    await waitMapStyleReady(m);
    ensureStyledRouteLayers(m, LAYER_IDS);
    setStyledRouteData(m, LAYER_IDS, paint, line, undefined, req.fill ?? null);
    applyStyledRouteLineScaleForContainer(m, LAYER_IDS, w, h);
  }
  const points = routeDisplayFitPoints(line, paint, req.fill ?? null);
  const fitPoints = points.length >= 2 ? points : line;
  const view =
    fitPoints.length >= 2
      ? fitRotated(fitPoints, bearing, {
          width: w,
          height: h,
          padding: PADDING,
          maxZoom: MAX_ZOOM,
        })
      : null;
  if (view) m.jumpTo({ ...view, pitch: 0 });
  else if (line.length >= 2) {
    const fallback = fitRotated(line, bearing, { width: w, height: h, padding: PADDING, maxZoom: MAX_ZOOM });
    if (fallback) m.jumpTo({ ...fallback, pitch: 0 });
    else m.jumpTo({ bearing, pitch: 0 });
  } else m.jumpTo({ bearing, pitch: 0 });
  m.resize();
  await waitMapIdle(m);
  return m.getCanvas().toDataURL("image/jpeg", 0.86);
}

async function renderSpotThumb(m: maplibregl.Map, req: Extract<RouteThumbnailRequest, { mode: "spot" }>, w: number, h: number): Promise<string> {
  ensureStyledRouteLayers(m, LAYER_IDS);
  setStyledRouteData(m, LAYER_IDS, null, [], undefined, null);
  const spots = req.spots;
  const view = listSpotThumbFitView(spots, w, h);
  if (view) m.jumpTo({ ...view, pitch: 0 });
  else {
    const points = spotCoordinates(spots);
    if (points.length === 1) m.jumpTo({ center: points[0]!, zoom: 15, bearing: 0, pitch: 0 });
  }
  m.resize();
  await waitMapIdle(m);
  return snapshotMapWithSpotMarkers(m, spots);
}

async function renderOne(req: RouteThumbnailRequest): Promise<string> {
  if (req.mode === "placeholder") return QUEST_LIST_THUMB_PLACEHOLDER;

  const { w, h, cacheKey } = thumbRenderSize();
  const id = cacheId(req, cacheKey);
  const hit = cache.get(id);
  if (hit) return hit;

  const m = ensureMap(w, h);
  await waitMapStyleReady(m);
  const url =
    req.mode === "spot" ? await renderSpotThumb(m, req, w, h) : await renderRouteThumb(m, req, w, h);
  cache.set(id, url);
  return url;
}

async function pump(): Promise<void> {
  if (pumping) return;
  pumping = true;
  while (queue.length > 0) {
    const job = queue.shift()!;
    try {
      job.resolve(await renderOne(job.req));
    } catch (err) {
      job.reject(err);
    }
  }
  pumping = false;
}

/** Cached JPEG data URL for a quest list thumbnail (one shared offscreen map, never per card). */
export function requestRouteThumbnail(req: RouteThumbnailRequest): Promise<string> {
  if (req.mode === "placeholder") return Promise.resolve(QUEST_LIST_THUMB_PLACEHOLDER);
  const { cacheKey } = thumbRenderSize();
  const id = cacheId(req, cacheKey);
  const hit = cache.get(id);
  if (hit) return Promise.resolve(hit);
  const pending = inflight.get(id);
  if (pending) return pending;
  const promise = new Promise<string>((resolve, reject) => {
    queue.push({ req, resolve, reject });
    void pump();
  }).finally(() => {
    inflight.delete(id);
  });
  inflight.set(id, promise);
  return promise;
}

export function observeRouteThumbnails(root: ParentNode): void {
  const imgs = root.querySelectorAll<HTMLImageElement>("img.nearby-item-thumb[data-thumb-id]:not([data-thumb-ready])");
  if (!imgs.length) return;
  const io = thumbObserverFor(root);
  for (const img of imgs) io.observe(img);
}

type ThumbLookup = (id: string) => RouteThumbnailRequest | null;

let thumbLookup: ThumbLookup = () => null;

export function setRouteThumbnailLookup(lookup: ThumbLookup): void {
  thumbLookup = lookup;
}

function applyThumbImage(img: HTMLImageElement, url: string, placeholder: boolean): void {
  img.src = url;
  img.classList.toggle("is-empty", false);
  img.classList.toggle("is-placeholder", placeholder);
}

async function hydrateThumb(img: HTMLImageElement): Promise<void> {
  const id = img.dataset.thumbId;
  if (!id || img.dataset.thumbReady === "1") return;
  const req = thumbLookup(id);
  if (!req) {
    applyThumbImage(img, QUEST_LIST_THUMB_PLACEHOLDER, true);
    img.dataset.thumbReady = "1";
    return;
  }
  if (req.mode === "placeholder") {
    applyThumbImage(img, QUEST_LIST_THUMB_PLACEHOLDER, true);
    img.dataset.thumbReady = "1";
    return;
  }
  if (req.mode === "route" && req.coordinates.length < 2) {
    applyThumbImage(img, QUEST_LIST_THUMB_PLACEHOLDER, true);
    img.dataset.thumbReady = "1";
    return;
  }
  if (req.mode === "spot" && spotCoordinates(req.spots).length < 1) {
    applyThumbImage(img, QUEST_LIST_THUMB_PLACEHOLDER, true);
    img.dataset.thumbReady = "1";
    return;
  }
  try {
    applyThumbImage(img, await requestRouteThumbnail(req), false);
  } catch {
    applyThumbImage(img, QUEST_LIST_THUMB_PLACEHOLDER, true);
  }
  img.dataset.thumbReady = "1";
}
